import { Capability } from '__types__';
import { rd } from '@passionware/monads';
import { useMemo } from 'react';
import type { ReportEntityType } from 'v5/api/report-templates/reportTemplates.api';
import type { FieldNameTypeMapping } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import type {
  ReportCustomFieldDefinitionInput,
  ReportCustomFieldGroupDefinitionInput,
  ReportEntityTypeInput
} from 'v5/core/reporting/reportFieldTree';
import type { AppScopeIdentity } from 'v5/core/app-scope/appScope.types';
import { MergeServices } from 'v5/platform/ts/services';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithBookingTypeService } from 'v5/services/io/BookingTypeService/BookingTypeService';

type ReportTemplateCatalogServices = MergeServices<
  [
    WithAppScopeService,
    WithAccessService,
    WithResourceTypeService,
    WithProductionTypeService,
    WithBookingTypeService,
    WithMetastructureMappingService
  ]
>;

type ReportTypedEntity = 'contact' | 'production' | 'booking';

type ReportCatalogType = ReportEntityTypeInput & { name: string };

/** What the editor offers, at the organisation's level: templates are global. */
interface ReportTemplateCatalog {
  /** Archived types included: a template may still be stored with one. */
  types: Record<ReportTypedEntity, ReportCatalogType[]>;
  customFieldDefinitions: ReportCustomFieldDefinitionInput[];
  customFieldGroupDefinitions: ReportCustomFieldGroupDefinitionInput[];
  /** Each custom field's type by its filter key (`custom_<id>`). */
  fieldTypes: FieldNameTypeMapping;
  allowCheckins: boolean;
}

export function reportTypedEntityOf(
  entityType: ReportEntityType
): ReportTypedEntity | null {
  switch (entityType) {
    case 'contact':
    case 'production':
    case 'booking':
      return entityType;
    case 'callout':
    case 'shortlist':
    case 'callsheet':
      return null;
  }
}

const catalogTypesOf = <
  T extends { id: number; name?: string; archived: boolean }
>(
  types: T[]
) => types.map(type => ({ ...type, name: type.name || `#${type.id}` }));

export function useReportTemplateCatalog(
  services: ReportTemplateCatalogServices
) {
  const organizationId = services.appScopeService.useOrganizationId();
  const organization = useMemo(
    (): AppScopeIdentity => ({ level: 'organization', organizationId }),
    [organizationId]
  );
  return rd.useMemoMap(
    rd.useCombine({
      resourceTypes:
        services.resourceTypeService.useResourceTypes(organization),
      productionTypes: services.productionTypeService.useProductionTypes(),
      bookingTypes: services.bookingTypeService.useBookingTypes(),
      definitions:
        services.metastructureMappingService.useCustomFieldDefinitions(),
      groupDefinitions:
        services.metastructureMappingService.useCustomFieldGroupDefinitions(),
      allowCheckins: services.accessService.useCan(organization)({
        action: Capability.CONFIGURE_CUSTOM_NIKE_FRONT_DESK
      })
    }),
    (catalog): ReportTemplateCatalog => {
      const definitions = Object.values(catalog.definitions ?? {});
      return {
        types: {
          contact: catalogTypesOf(catalog.resourceTypes),
          production: catalogTypesOf(catalog.productionTypes),
          booking: catalogTypesOf(catalog.bookingTypes)
        },
        customFieldDefinitions: definitions,
        customFieldGroupDefinitions: Object.values(
          catalog.groupDefinitions ?? {}
        ),
        fieldTypes: Object.fromEntries(
          definitions.map(definition => [
            `custom_${definition.id}`,
            definition.data_type
          ])
        ),
        allowCheckins: catalog.allowCheckins
      };
    }
  );
}
